import React, { useState, useCallback, useEffect } from 'react';
import { X, Phone, Gift, Check, Loader2, AlertCircle } from 'lucide-react';

/**
 * LoyaltyModal - Phone number entry for loyalty lookup
 * 
 * Features:
 * - Full-screen modal overlay
 * - Phone number input with formatting
 * - Lookup with loading state
 * - Success/error feedback
 * - Points balance display
 */
export default function LoyaltyModal({
  isOpen,
  onClose,
  onLookup,
  currentPhone = '',
  currentLoyaltyInfo = null,
}) {
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPhone(currentPhone || '');
      setError('');
      setShowSuccess(false);
    }
  }, [isOpen, currentPhone]);

  // Format phone number
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
    setError('');
  };

  const getDigits = useCallback(() => phone.replace(/\D/g, ''), [phone]);

  const handleLookup = useCallback(async () => {
    const digits = getDigits();
    
    if (digits.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await onLookup(digits);
      
      if (result?.success) {
        setShowSuccess(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(result?.message || 'No account found with this number');
      }
    } catch (err) {
      setError('Failed to look up account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [getDigits, onLookup, onClose]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleLookup();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="loyalty-modal-title"
    >
      <div className="w-full max-w-md mx-4 bg-[#1a1f26] border border-white/10 rounded-3xl overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#f5a623]/10 flex items-center justify-center">
              <Gift size={24} className="text-[#f5a623]" />
            </div>
            <div>
              <h2 id="loyalty-modal-title" className="text-xl font-semibold text-white">
                Rewards Account
              </h2>
              <p className="text-sm text-gray-400">Earn points on your order</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {showSuccess ? (
            /* Success State */
            <div className="text-center py-8 animate-scale-in">
              <div className="w-20 h-20 rounded-full bg-[#10b981]/20 flex items-center justify-center mx-auto mb-4">
                <Check size={40} className="text-[#10b981]" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-2">
                Welcome back!
              </h3>
              <p className="text-gray-400">
                Your rewards account is linked
              </p>
            </div>
          ) : (
            <>
              {/* Phone Input */}
              <div className="mb-4">
                <label htmlFor="loyalty-phone" className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    id="loyalty-phone"
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    onKeyDown={handleKeyDown}
                    placeholder="(555) 123-4567"
                    disabled={isLoading}
                    autoFocus
                    className="
                      w-full h-16 pl-12 pr-4
                      bg-[#252b33] border-2 border-white/10
                      rounded-xl
                      text-white text-xl
                      placeholder-gray-500
                      focus:border-[#f5a623] focus:outline-none
                      disabled:opacity-50
                      transition-colors
                    "
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-[#ef4444]/10 border border-[#ef4444]/20">
                  <AlertCircle size={18} className="text-[#ef4444] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#ef4444]">{error}</p>
                </div>
              )}

              {/* Current Loyalty Info */}
              {currentLoyaltyInfo && (
                <div className="mb-4 p-4 rounded-xl bg-[#10b981]/10 border border-[#10b981]/20">
                  <p className="text-sm text-[#10b981] font-medium">
                    Currently linked: {currentLoyaltyInfo.customer_name}
                  </p>
                  <p className="text-sm text-[#10b981]/80">
                    {currentLoyaltyInfo.points_balance || 0} points
                  </p>
                </div>
              )}

              {/* Info Text */}
              <p className="text-sm text-gray-500 text-center mb-6">
                Enter the phone number associated with your rewards account
              </p>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="
                    flex-1 h-14
                    bg-[#252b33] border border-white/10
                    text-white font-medium
                    rounded-xl
                    hover:bg-[#2d343d]
                    transition-colors
                  "
                >
                  {currentLoyaltyInfo ? 'Keep Current' : 'Skip'}
                </button>
                <button
                  onClick={handleLookup}
                  disabled={isLoading || getDigits().length < 10}
                  className="
                    flex-1 h-14
                    bg-[#f5a623] text-[#0f1419]
                    font-semibold rounded-xl
                    disabled:opacity-50 disabled:cursor-not-allowed
                    hover:brightness-110
                    active:scale-[0.98]
                    transition-all
                  "
                >
                  {isLoading ? (
                    <Loader2 size={22} className="animate-spin mx-auto" />
                  ) : (
                    'Look Up'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Animation */}
      <style>{`
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
